<template>
  <div class="reembolsos-lista">
    <div class="page-header">
      <h1 class="page-title">
        <i class="fas fa-money-bill-wave"></i>
        Gestão de Reembolsos
      </h1>
      <p class="page-subtitle">Gerencie as solicitações de restituição de pagamentos</p>
    </div>

    <!-- Estatísticas -->
    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-icon total">
          <i class="fas fa-file-invoice-dollar"></i>
        </div>
        <div class="stat-content">
          <h3>{{ estatisticas.total || 0 }}</h3>
          <p>Total de Solicitações</p>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon pending">
          <i class="fas fa-clock"></i>
        </div>
        <div class="stat-content">
          <h3>{{ estatisticas.solicitados || 0 }}</h3>
          <p>Pendentes</p>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon processing">
          <i class="fas fa-spinner"></i>
        </div>
        <div class="stat-content">
          <h3>{{ estatisticas.processando || 0 }}</h3>
          <p>Processando</p>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon approved">
          <i class="fas fa-check-circle"></i>
        </div>
        <div class="stat-content">
          <h3>{{ estatisticas.efetuados || 0 }}</h3>
          <p>Efetuados</p>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon denied">
          <i class="fas fa-times-circle"></i>
        </div>
        <div class="stat-content">
          <h3>{{ estatisticas.negados || 0 }}</h3>
          <p>Negados</p>
        </div>
      </div>

      <div class="stat-card total-value">
        <div class="stat-icon money">
          <i class="fas fa-dollar-sign"></i>
        </div>
        <div class="stat-content">
          <h3>{{ formatarMoeda(estatisticas.valor_total_reembolsado || 0) }}</h3>
          <p>Total Reembolsado</p>
        </div>
      </div>
    </div>

    <!-- Filtros -->
    <div class="filters-card">
      <div class="filters-row">
        <div class="filter-group">
          <label>Status</label>
          <select v-model="filters.status" class="form-control" @change="applyFilters">
            <option value="">Todos</option>
            <option value="Solicitado">Solicitado</option>
            <option value="Processando">Processando</option>
            <option value="Efetuado">Efetuado</option>
            <option value="Negado">Negado</option>
          </select>
        </div>

        <div class="filter-group">
          <label>Candidato</label>
          <input 
            v-model="filters.candidato" 
            type="text" 
            placeholder="Nome ou CPF"
            class="form-control"
            @input="debounceSearch"
          />
        </div>

        <div class="filter-actions">
          <button @click="clearFilters" class="btn btn-secondary">
            <i class="fas fa-times"></i>
            Limpar
          </button>
          <button @click="exportData" class="btn btn-success">
            <i class="fas fa-download"></i>
            Exportar
          </button>
          <button @click="carregarReembolsos" class="btn btn-primary">
            <i class="fas fa-sync-alt"></i>
            Atualizar
          </button>
        </div>
      </div>
    </div>

    <!-- Tabela de Reembolsos -->
    <div class="table-card">
      <div v-if="loading" class="loading-state">
        <i class="fas fa-spinner fa-spin"></i>
        <p>Carregando reembolsos...</p>
      </div>

      <div v-else-if="reembolsosFiltrados.length === 0" class="empty-state">
        <i class="fas fa-money-bill-wave"></i>
        <h3>Nenhum reembolso encontrado</h3>
        <p>Não há solicitações que correspondam aos filtros aplicados.</p>
      </div>

      <div v-else>
        <div class="table-responsive">
          <table class="table table-hover">
            <thead>
              <tr>
                <th style="width: 80px">ID</th>
                <th>Candidato</th>
                <th>Concurso</th>
                <th>Vaga</th>
                <th style="width: 120px">Valor</th>
                <th style="width: 140px">Data Solicitação</th>
                <th style="width: 130px">Status</th>
                <th style="width: 140px" class="text-center">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="reembolso in reembolsosFiltrados" :key="reembolso.id">
                <td><strong>#{{ reembolso.id }}</strong></td>
                <td>
                  <div class="user-info">
                    <strong>{{ reembolso.inscricao?.cadastro?.nome || 'N/A' }}</strong>
                    <small>{{ formatarCPF(reembolso.inscricao?.cadastro?.cpf) }}</small>
                  </div>
                </td>
                <td>
                  <span class="text-truncate" style="max-width: 200px; display: block;">
                    {{ reembolso.inscricao?.concurso?.titulo || 'N/A' }}
                  </span>
                </td>
                <td>
                  <span class="text-truncate" style="max-width: 150px; display: block;">
                    {{ reembolso.inscricao?.vaga?.cargo || 'N/A' }}
                  </span>
                </td>
                <td>
                  <strong class="text-success">{{ formatarMoeda(reembolso.valor_reembolso) }}</strong>
                </td>
                <td>
                  <small>{{ formatarData(reembolso.data_solicitacao || reembolso.created_at) }}</small>
                </td>
                <td>
                  <span :class="['badge', getBadgeClass(reembolso.status)]">
                    <i :class="getStatusIcon(reembolso.status)"></i>
                    {{ reembolso.status }}
                  </span>
                </td>
                <td class="text-center">
                  <button 
                    @click="visualizarDetalhes(reembolso)" 
                    class="btn btn-sm btn-info"
                    title="Ver Detalhes"
                  >
                    <i class="fas fa-eye"></i>
                  </button>
                  <button 
                    v-if="reembolso.status !== 'Efetuado' && reembolso.status !== 'Negado'"
                    @click="abrirModalStatus(reembolso)" 
                    class="btn btn-sm btn-warning"
                    title="Alterar Status"
                  >
                    <i class="fas fa-edit"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Modal Detalhes -->
    <div v-if="showModalDetalhes" class="modal-overlay" @click.self="fecharModalDetalhes">
      <div class="modal-content modal-detalhes">
        <div class="modal-header">
          <h2>
            <i class="fas fa-file-invoice-dollar"></i>
            Detalhes do Reembolso #{{ reembolsoSelecionado.id }}
          </h2>
          <button @click="fecharModalDetalhes" class="btn-close">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="modal-body">
          <div class="detalhes-section">
            <h4><i class="fas fa-user"></i> Dados do Candidato</h4>
            <div class="detalhes-grid">
              <div class="detalhe-item">
                <label>Nome:</label>
                <span>{{ reembolsoSelecionado.inscricao?.cadastro?.nome }}</span>
              </div>
              <div class="detalhe-item">
                <label>CPF:</label>
                <span>{{ formatarCPF(reembolsoSelecionado.inscricao?.cadastro?.cpf) }}</span>
              </div>
              <div class="detalhe-item">
                <label>E-mail:</label>
                <span>{{ reembolsoSelecionado.inscricao?.cadastro?.email }}</span>
              </div>
              <div class="detalhe-item">
                <label>Telefone:</label>
                <span>{{ reembolsoSelecionado.inscricao?.cadastro?.telefone }}</span>
              </div>
            </div>
          </div>

          <div class="detalhes-section">
            <h4><i class="fas fa-clipboard-list"></i> Dados da Inscrição</h4>
            <div class="detalhes-grid">
              <div class="detalhe-item full-width">
                <label>Concurso:</label>
                <span>{{ reembolsoSelecionado.inscricao?.concurso?.titulo }}</span>
              </div>
              <div class="detalhe-item">
                <label>Vaga:</label>
                <span>{{ reembolsoSelecionado.inscricao?.vaga?.cargo }}</span>
              </div>
              <div class="detalhe-item">
                <label>Inscrição ID:</label>
                <span>#{{ reembolsoSelecionado.inscricao_id }}</span>
              </div>
            </div>
          </div>

          <div class="detalhes-section">
            <h4><i class="fas fa-money-bill-wave"></i> Dados do Reembolso</h4>
            <div class="detalhes-grid">
              <div class="detalhe-item">
                <label>Valor:</label>
                <span class="text-success"><strong>{{ formatarMoeda(reembolsoSelecionado.valor_reembolso) }}</strong></span>
              </div>
              <div class="detalhe-item">
                <label>Status:</label>
                <span :class="['badge', getBadgeClass(reembolsoSelecionado.status)]">
                  {{ reembolsoSelecionado.status }}
                </span>
              </div>
              <div class="detalhe-item">
                <label>Data Solicitação:</label>
                <span>{{ formatarDataHora(reembolsoSelecionado.data_solicitacao || reembolsoSelecionado.created_at) }}</span>
              </div>
              <div class="detalhe-item">
                <label>Data Processamento:</label>
                <span>{{ reembolsoSelecionado.data_processamento ? formatarDataHora(reembolsoSelecionado.data_processamento) : 'Não processado' }}</span>
              </div>
              <div class="detalhe-item full-width">
                <label>Motivo:</label>
                <p class="motivo-text">{{ reembolsoSelecionado.motivo || 'Não informado' }}</p>
              </div>
            </div>
          </div>

          <div class="detalhes-section">
            <h4><i class="fas fa-university"></i> Dados Bancários</h4>
            <div class="detalhes-grid">
              <div class="detalhe-item">
                <label>Banco:</label>
                <span>{{ reembolsoSelecionado.banco || 'N/A' }}</span>
              </div>
              <div class="detalhe-item">
                <label>Agência:</label>
                <span>{{ reembolsoSelecionado.agencia || 'N/A' }}</span>
              </div>
              <div class="detalhe-item">
                <label>Conta:</label>
                <span>{{ reembolsoSelecionado.conta || 'N/A' }}</span>
              </div>
              <div class="detalhe-item">
                <label>Tipo Conta:</label>
                <span>{{ reembolsoSelecionado.tipo_conta || 'N/A' }}</span>
              </div>
              <div class="detalhe-item">
                <label>Chave PIX:</label>
                <span>{{ reembolsoSelecionado.chavepix || 'N/A' }}</span>
              </div>
              <div class="detalhe-item">
                <label>Titular:</label>
                <span>{{ reembolsoSelecionado.titular_nome || 'N/A' }}</span>
              </div>
              <div class="detalhe-item">
                <label>CPF Titular:</label>
                <span>{{ formatarCPF(reembolsoSelecionado.titular_documento) || 'N/A' }}</span>
              </div>
            </div>
          </div>

          <div v-if="reembolsoSelecionado.observacao_admin" class="detalhes-section">
            <h4><i class="fas fa-comment-alt"></i> Observações do Administrador</h4>
            <p class="observacao-text">{{ reembolsoSelecionado.observacao_admin }}</p>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="fecharModalDetalhes" class="btn btn-secondary">
            <i class="fas fa-times"></i>
            Fechar
          </button>
          <button 
            v-if="reembolsoSelecionado.status !== 'Efetuado' && reembolsoSelecionado.status !== 'Negado'"
            @click="abrirModalStatus(reembolsoSelecionado)" 
            class="btn btn-primary"
          >
            <i class="fas fa-edit"></i>
            Alterar Status
          </button>
        </div>
      </div>
    </div>

    <!-- Modal Alterar Status -->
    <div v-if="showModalStatus" class="modal-overlay" @click.self="fecharModalStatus">
      <div class="modal-content modal-status">
        <div class="modal-header">
          <h2>
            <i class="fas fa-exchange-alt"></i>
            Alterar Status do Reembolso
          </h2>
          <button @click="fecharModalStatus" class="btn-close">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="modal-body">
          <div class="alert alert-info">
            <i class="fas fa-info-circle"></i>
            Candidato: <strong>{{ reembolsoSelecionado.inscricao?.cadastro?.nome }}</strong><br>
            Valor: <strong>{{ formatarMoeda(reembolsoSelecionado.valor_reembolso) }}</strong>
          </div>

          <form @submit.prevent="atualizarStatus">
            <div class="form-group">
              <label>Novo Status *</label>
              <select v-model="formStatus.status" class="form-control" required>
                <option value="">Selecione...</option>
                <option value="Solicitado">Solicitado</option>
                <option value="Processando">Processando</option>
                <option value="Efetuado">Efetuado</option>
                <option value="Negado">Negado</option>
              </select>
            </div>

            <div class="form-group">
              <label>Observações</label>
              <textarea 
                v-model="formStatus.observacao_admin" 
                class="form-control"
                rows="4"
                placeholder="Adicione observações sobre esta alteração (opcional)"
              ></textarea>
            </div>

            <div v-if="formStatus.status === 'Efetuado'" class="form-group">
              <label>Comprovante de Reembolso</label>
              <input 
                v-model="formStatus.comprovante_reembolso" 
                type="text" 
                class="form-control"
                placeholder="URL ou número do comprovante"
              />
            </div>

            <div class="modal-footer">
              <button type="button" @click="fecharModalStatus" class="btn btn-secondary">
                <i class="fas fa-times"></i>
                Cancelar
              </button>
              <button type="submit" class="btn btn-primary" :disabled="updatingStatus">
                <i :class="updatingStatus ? 'fas fa-spinner fa-spin' : 'fas fa-check'"></i>
                {{ updatingStatus ? 'Atualizando...' : 'Atualizar Status' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'ReembolsosLista',
  
  data() {
    return {
      reembolsos: [],
      estatisticas: {},
      loading: false,
      updatingStatus: false,
      filters: {
        status: '',
        candidato: ''
      },
      searchTimeout: null,
      showModalDetalhes: false,
      showModalStatus: false,
      reembolsoSelecionado: {},
      formStatus: {
        status: '',
        observacao_admin: '',
        comprovante_reembolso: ''
      }
    };
  },

  computed: {
    reembolsosFiltrados() {
      let filtered = [...this.reembolsos];

      if (this.filters.status) {
        filtered = filtered.filter(r => r.status === this.filters.status);
      }

      if (this.filters.candidato) {
        const search = this.filters.candidato.toLowerCase();
        filtered = filtered.filter(r => {
          const nome = r.inscricao?.cadastro?.nome?.toLowerCase() || '';
          const cpf = r.inscricao?.cadastro?.cpf || '';
          return nome.includes(search) || cpf.includes(search);
        });
      }

      return filtered;
    }
  },

  mounted() {
    this.carregarEstatisticas();
    this.carregarReembolsos();
  },

  methods: {
    async carregarReembolsos() {
      this.loading = true;
      try {
        const response = await axios.get('/ps/reembolso/admin/todos');
        this.reembolsos = response.data;
      } catch (error) {
        console.error('Erro ao carregar reembolsos:', error);
        this.$sweet.showError('Erro ao carregar reembolsos. Tente novamente.');
      } finally {
        this.loading = false;
      }
    },

    async carregarEstatisticas() {
      try {
        const response = await axios.get('/ps/reembolso/admin/estatisticas');
        this.estatisticas = response.data;
      } catch (error) {
        console.error('Erro ao carregar estatísticas:', error);
      }
    },

    applyFilters() {
      // Filtros aplicados via computed property
    },

    debounceSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.applyFilters();
      }, 500);
    },

    clearFilters() {
      this.filters = {
        status: '',
        candidato: ''
      };
    },

    async exportData() {
      try {
        this.$sweet.showInfo('Gerando arquivo Excel...')
        
        const response = await axios.get('/ps/reembolso/admin/exportar-excel', {
          params: this.filters,
          responseType: 'blob'
        })
        
        // Criar download do arquivo
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `reembolsos_${new Date().getTime()}.csv`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        
        this.$sweet.showSuccess('Arquivo exportado com sucesso!')
      } catch (error) {
        console.error('Erro ao exportar dados:', error)
        this.$sweet.showError('Erro ao exportar dados')
      }
    },

    visualizarDetalhes(reembolso) {
      this.reembolsoSelecionado = { ...reembolso };
      this.showModalDetalhes = true;
    },

    fecharModalDetalhes() {
      this.showModalDetalhes = false;
      this.reembolsoSelecionado = {};
    },

    abrirModalStatus(reembolso) {
      this.reembolsoSelecionado = { ...reembolso };
      this.formStatus = {
        status: reembolso.status,
        observacao_admin: reembolso.observacao_admin || '',
        comprovante_reembolso: reembolso.comprovante_reembolso || ''
      };
      this.showModalStatus = true;
      this.showModalDetalhes = false;
    },

    fecharModalStatus() {
      this.showModalStatus = false;
      this.formStatus = {
        status: '',
        observacao_admin: '',
        comprovante_reembolso: ''
      };
    },

    async atualizarStatus() {
      if (!this.formStatus.status) {
        this.$sweet.showError('Selecione um status');
        return;
      }

      this.updatingStatus = true;
      try {
        await axios.put(`/ps/reembolso/admin/${this.reembolsoSelecionado.id}/status`, this.formStatus);

        this.$sweet.showSuccess('Status atualizado com sucesso!');
        this.fecharModalStatus();
        await this.carregarReembolsos();
        await this.carregarEstatisticas();

      } catch (error) {
        console.error('Erro ao atualizar status:', error);
        const errorMsg = error.response?.data?.error || 'Erro ao atualizar status. Tente novamente.';
        this.$sweet.showError(errorMsg);
      } finally {
        this.updatingStatus = false;
      }
    },

    getBadgeClass(status) {
      const classes = {
        'Solicitado': 'badge-warning',
        'Processando': 'badge-info',
        'Efetuado': 'badge-success',
        'Negado': 'badge-danger'
      };
      return classes[status] || 'badge-secondary';
    },

    getStatusIcon(status) {
      const icons = {
        'Solicitado': 'fas fa-clock',
        'Processando': 'fas fa-spinner',
        'Efetuado': 'fas fa-check-circle',
        'Negado': 'fas fa-times-circle'
      };
      return icons[status] || 'fas fa-question-circle';
    },

    formatarMoeda(valor) {
      if (!valor) return 'R$ 0,00';
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(valor);
    },

    formatarCPF(cpf) {
      if (!cpf) return 'N/A';
      return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    },

    formatarData(data) {
      if (!data) return 'N/A';
      
      try {
        // Se a data vier apenas com a data (sem hora), usar diretamente
        if (typeof data === 'string' && data.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const [year, month, day] = data.split('-');
          return `${day}/${month}/${year}`;
        }
        
        // Para datas ISO com timezone, remover o 'Z' e tratar como local
        let dateString = data;
        if (typeof data === 'string' && data.includes('T')) {
          dateString = dateString.replace('Z', '');
          if (dateString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
            const [datePart] = dateString.split('T');
            const [year, month, day] = datePart.split('-');
            return `${day}/${month}/${year}`;
          }
        }
        
        // Criar data e usar métodos locais
        const dateObj = new Date(dateString);
        if (isNaN(dateObj.getTime())) return 'N/A';
        
        const dia = String(dateObj.getDate()).padStart(2, '0');
        const mes = String(dateObj.getMonth() + 1).padStart(2, '0');
        const ano = dateObj.getFullYear();
        return `${dia}/${mes}/${ano}`;
      } catch (error) {
        return 'N/A';
      }
    },

    formatarDataHora(data) {
      if (!data) return 'N/A';
      
      try {
        // Se a data vier apenas com a data (sem hora), usar diretamente
        if (typeof data === 'string' && data.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const [year, month, day] = data.split('-');
          return `${day}/${month}/${year} 00:00`;
        }
        
        // Tratar formato do banco: 2025-11-25 07:53:15.000
        if (typeof data === 'string') {
          // Remover o 'Z' do final se existir (formato ISO)
          let dateString = data.replace('Z', '');
          
          // Se tiver formato com espaço (formato do banco: YYYY-MM-DD HH:mm:ss)
          if (dateString.match(/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}/)) {
            const [datePart, timePart] = dateString.split(' ');
            const [year, month, day] = datePart.split('-');
            const [hours, minutes] = timePart.split(':');
            return `${day}/${month}/${year} ${hours}:${minutes}`;
          }
          
          // Se tiver formato ISO com T (YYYY-MM-DDTHH:mm:ss)
          if (dateString.includes('T')) {
            const [datePart, timePart] = dateString.split('T');
            const [year, month, day] = datePart.split('-');
            const [hours, minutes] = timePart.split(':');
            return `${day}/${month}/${year} ${hours}:${minutes}`;
          }
        }
        
        // Fallback: usar Date object apenas se necessário
        const dateObj = new Date(data);
        if (isNaN(dateObj.getTime())) return 'N/A';
        
        const dia = String(dateObj.getDate()).padStart(2, '0');
        const mes = String(dateObj.getMonth() + 1).padStart(2, '0');
        const ano = dateObj.getFullYear();
        const hora = String(dateObj.getHours()).padStart(2, '0');
        const minuto = String(dateObj.getMinutes()).padStart(2, '0');
        return `${dia}/${mes}/${ano} ${hora}:${minuto}`;
      } catch (error) {
        return 'N/A';
      }
    }
  }
};
</script>

<style lang="sass" scoped>
.reembolsos-lista
  padding: 2rem

.page-header
  margin-bottom: 2rem

  .page-title
    display: flex
    align-items: center
    gap: 1rem
    font-size: 2rem
    font-weight: 700
    color: #2c3e50
    margin-bottom: 0.5rem

    i
      color: #28a745

  .page-subtitle
    color: #6c757d
    font-size: 1rem

.stats-grid
  display: grid
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr))
  gap: 1.5rem
  margin-bottom: 2rem

.stat-card
  background: white
  border-radius: 12px
  padding: 1.5rem
  display: flex
  align-items: center
  gap: 1rem
  box-shadow: 0 2px 8px rgba(0,0,0,0.08)
  transition: transform 0.3s ease, box-shadow 0.3s ease

  &:hover
    transform: translateY(-4px)
    box-shadow: 0 4px 16px rgba(0,0,0,0.12)

  &.total-value
    grid-column: span 2

  .stat-icon
    width: 60px
    height: 60px
    border-radius: 12px
    display: flex
    align-items: center
    justify-content: center
    font-size: 1.5rem
    color: white

    &.total
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%)

    &.pending
      background: linear-gradient(135deg, #ffc107 0%, #ff9800 100%)

    &.processing
      background: linear-gradient(135deg, #17a2b8 0%, #138496 100%)

    &.approved
      background: linear-gradient(135deg, #28a745 0%, #20c997 100%)

    &.denied
      background: linear-gradient(135deg, #dc3545 0%, #c82333 100%)

    &.money
      background: linear-gradient(135deg, #20c997 0%, #17a2b8 100%)

  .stat-content
    h3
      font-size: 1.8rem
      font-weight: 700
      margin: 0
      color: #2c3e50

    p
      margin: 0
      color: #6c757d
      font-size: 0.9rem

.filters-card
  background: white
  border-radius: 12px
  padding: 1.5rem
  margin-bottom: 2rem
  box-shadow: 0 2px 8px rgba(0,0,0,0.08)

.filters-row
  display: grid
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr))
  gap: 1rem
  align-items: end

.filter-group
  label
    display: block
    font-weight: 600
    margin-bottom: 0.5rem
    color: #495057
    font-size: 0.9rem

  .form-control
    width: 100%
    padding: 0.6rem
    border: 1px solid #ced4da
    border-radius: 8px
    font-size: 0.9rem
    transition: border-color 0.3s ease, box-shadow 0.3s ease

    &:focus
      outline: none
      border-color: #28a745
      box-shadow: 0 0 0 3px rgba(40, 167, 69, 0.1)

.filter-actions
  display: flex
  gap: 0.5rem

  .btn
    padding: 0.6rem 1.2rem
    border: none
    border-radius: 8px
    font-weight: 600
    cursor: pointer
    transition: all 0.3s ease
    display: inline-flex
    align-items: center
    gap: 0.5rem

    &.btn-secondary
      background: #6c757d
      color: white

      &:hover
        background: #5a6268

    &.btn-primary
      background: #28a745
      color: white

      &:hover
        background: #218838

.table-card
  background: white
  border-radius: 12px
  padding: 1.5rem
  box-shadow: 0 2px 8px rgba(0,0,0,0.08)

.loading-state, .empty-state
  text-align: center
  padding: 4rem 2rem
  color: #6c757d

  i
    font-size: 3rem
    margin-bottom: 1rem
    opacity: 0.5

  h3
    font-size: 1.5rem
    margin-bottom: 0.5rem

.table-responsive
  overflow-x: auto

.table
  width: 100%
  border-collapse: collapse
  font-size: 0.9rem

  thead
    background: #f8f9fa
    border-bottom: 2px solid #dee2e6

    th
      padding: 1rem 0.75rem
      text-align: left
      font-weight: 600
      color: #495057

  tbody
    tr
      border-bottom: 1px solid #dee2e6
      transition: background-color 0.2s ease

      &:hover
        background-color: #f8f9fa

    td
      padding: 1rem 0.75rem

.user-info
  display: flex
  flex-direction: column
  gap: 0.2rem

  strong
    font-weight: 600
    color: #2c3e50

  small
    color: #6c757d
    font-size: 0.85rem

.badge
  display: inline-flex
  align-items: center
  gap: 0.4rem
  padding: 0.4rem 0.8rem
  border-radius: 6px
  font-size: 0.8rem
  font-weight: 600

  &.badge-warning
    background: #fff3cd
    color: #856404

  &.badge-info
    background: #d1ecf1
    color: #0c5460

  &.badge-success
    background: #d4edda
    color: #155724

  &.badge-danger
    background: #f8d7da
    color: #721c24

.btn-sm
  padding: 0.4rem 0.8rem
  font-size: 0.85rem
  border: none
  border-radius: 6px
  cursor: pointer
  transition: all 0.3s ease
  margin-right: 0.25rem

  &.btn-info
    background: #17a2b8
    color: white

    &:hover
      background: #138496

  &.btn-warning
    background: #ffc107
    color: #212529

    &:hover
      background: #e0a800

.modal-overlay
  position: fixed
  top: 0
  left: 0
  width: 100%
  height: 100%
  background: rgba(0, 0, 0, 0.6)
  display: flex
  align-items: center
  justify-content: center
  z-index: 10000
  padding: 20px

.modal-content
  background: white
  border-radius: 12px
  width: 100%
  max-width: 900px
  max-height: 90vh
  overflow-y: auto
  box-shadow: 0 10px 40px rgba(0,0,0,0.3)
  animation: slideInDown 0.3s ease

  &.modal-status
    max-width: 600px

@keyframes slideInDown
  from
    opacity: 0
    transform: translateY(-30px)
  to
    opacity: 1
    transform: translateY(0)

.modal-header
  display: flex
  justify-content: space-between
  align-items: center
  padding: 1.5rem
  border-bottom: 1px solid #dee2e6

  h2
    display: flex
    align-items: center
    gap: 0.8rem
    margin: 0
    font-size: 1.5rem
    color: #2c3e50

    i
      color: #28a745

  .btn-close
    background: none
    border: none
    font-size: 1.5rem
    color: #6c757d
    cursor: pointer
    transition: color 0.3s ease

    &:hover
      color: #dc3545

.modal-body
  padding: 1.5rem

.detalhes-section
  margin-bottom: 2rem
  padding-bottom: 1.5rem
  border-bottom: 1px solid #e9ecef

  &:last-child
    border-bottom: none

  h4
    display: flex
    align-items: center
    gap: 0.5rem
    color: #495057
    margin-bottom: 1rem
    font-size: 1.1rem

    i
      color: #28a745

.detalhes-grid
  display: grid
  grid-template-columns: repeat(2, 1fr)
  gap: 1rem

.detalhe-item
  &.full-width
    grid-column: 1 / -1

  label
    display: block
    font-weight: 600
    color: #495057
    margin-bottom: 0.3rem
    font-size: 0.9rem

  span, p
    color: #2c3e50

.motivo-text, .observacao-text
  background: #f8f9fa
  padding: 1rem
  border-radius: 8px
  margin: 0

.alert
  padding: 1rem
  border-radius: 8px
  margin-bottom: 1.5rem
  display: flex
  align-items: flex-start
  gap: 0.8rem

  &.alert-info
    background: #d1ecf1
    border: 1px solid #bee5eb
    color: #0c5460

.form-group
  margin-bottom: 1.5rem

  label
    display: block
    font-weight: 600
    color: #495057
    margin-bottom: 0.5rem

  .form-control
    width: 100%
    padding: 0.75rem
    border: 1px solid #ced4da
    border-radius: 8px
    font-size: 0.95rem

    &:focus
      outline: none
      border-color: #28a745
      box-shadow: 0 0 0 3px rgba(40, 167, 69, 0.1)

  textarea.form-control
    resize: vertical

.modal-footer
  padding: 1.5rem
  border-top: 1px solid #dee2e6
  display: flex
  justify-content: flex-end
  gap: 1rem

  .btn
    padding: 0.75rem 1.5rem
    border: none
    border-radius: 8px
    font-weight: 600
    cursor: pointer
    transition: all 0.3s ease
    display: inline-flex
    align-items: center
    gap: 0.5rem

    &:disabled
      opacity: 0.6
      cursor: not-allowed

    &.btn-secondary
      background: #6c757d
      color: white

      &:hover:not(:disabled)
        background: #5a6268

    &.btn-primary
      background: #28a745
      color: white

      &:hover:not(:disabled)
        background: #218838

@media (max-width: 768px)
  .reembolsos-lista
    padding: 1rem

  .stats-grid
    grid-template-columns: 1fr

  .stat-card.total-value
    grid-column: span 1

  .filters-row
    grid-template-columns: 1fr

  .detalhes-grid
    grid-template-columns: 1fr

  .modal-content
    max-width: 95%
</style>

